/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/
import React from "react";
import PropTypes from "prop-types";
import Text from "../GUI/Text";
import "aframe-event-set-component";
import { Vector3 } from "three";

function BlockChain({
    data,
    width,
    height,
    clickHandler,
    selected,
    deviceData,
    colorSelector,
    iconSelector,
    aframeClasses,
    Tooltip,
    ...otherAttributes
}) {
    if (!data) return null;
    const isVROrMobile =
        deviceData.isMobile || deviceData.areControllersConnected;

    const ELEMENTS_NUMBER = data.length < 4 ? 4 : data.length;
    const ELEMENT_WIDTH = width / ELEMENTS_NUMBER;
    const BLOCK_WIDTH = ELEMENT_WIDTH * 0.8;
    const BLOCK_DEPTH = height / 2;
    const ARROW_WIDTH = ELEMENT_WIDTH * 0.2;
    const ARROW_RADIUS = height / 40;

    const center =
        (ELEMENTS_NUMBER / 2) % 2 === 0
            ? Math.ceil(ELEMENTS_NUMBER / 2)
            : Math.floor(ELEMENTS_NUMBER / 2);

    const drawBlocks = () => {
        return data.map((element, id) => {
            const rotatingAngle = isVROrMobile ? (id - center) * -10 : 0;
            const rotatingPosition = isVROrMobile
                ? (Math.pow(id - center, 2) * height) / 4
                : 0;
            return (
                <a-entity
                    key={id}
                    class={aframeClasses}
                    rotation={`0 ${rotatingAngle} 0`}
                    position={`${
                        id * (width / ELEMENTS_NUMBER)
                    } 0 ${rotatingPosition}`}
                    scale={selected === id ? "1.15 1.15 1.15" : "1 1 1"}
                    animation__mouseenter={`property: scale; startEvents: mouseenter; to: 1.15 1.15 1.15; dur: 150; enabled: ${
                        selected !== id
                    }`}
                    animation__mouseleave={`property: scale; startEvents: mouseleave; to: 1 1 1; dur: 150; enabled: ${
                        selected !== id
                    }`}
                    onClick={() => {
                        if (clickHandler) clickHandler(id);
                    }}
                    event-set__mouseenter={`_target: #Tooltip${id}; _delay: 150; visible: true`}
                    event-set__mouseleave={`_target: #Tooltip${id}; _delay: 150; visible: false`}
                >
                    <a-entity
                        position={`0 ${(height / 2) * 1.5 + height} ${
                            BLOCK_DEPTH / 2
                        }`}
                        id={`Tooltip${id}`}
                        visible={false}
                    >
                        <Tooltip
                            width={width / 4}
                            height={height * 2}
                            element={element}
                        />
                    </a-entity>

                    <a-box
                        width={BLOCK_WIDTH}
                        height={height * 0.3}
                        color={colorSelector(element.status)}
                        depth={BLOCK_DEPTH}
                        position={`0 ${height / 2 - height * 0.15} 0`}
                    >
                        <a-image
                            src={iconSelector(element.status)}
                            width={height * 0.2}
                            height={height * 0.2}
                            position={`0 0 ${BLOCK_DEPTH / 2 + 0.001}`}
                        />
                    </a-box>
                    <a-box
                        width={BLOCK_WIDTH}
                        height={height * 0.7}
                        depth={BLOCK_DEPTH}
                        color={selected === id ? "#474747" : "#262626"}
                        position={`0 ${
                            height / 2 - height * 0.3 - height * 0.35
                        } 0`}
                    >
                        <Text
                            width={BLOCK_WIDTH * 0.9}
                            align="center"
                            sizeDecimal={32 / BLOCK_WIDTH}
                            text={`${element.workstation}#${element.workflow}\n${element.job}`}
                            position={`0 0 ${BLOCK_DEPTH / 2 + 0.001}`}
                        />
                    </a-box>
                </a-entity>
            );
        });
    };

    const drawArrows = () => {
        return data.slice(0, -1).map((element, id) => {
            const rotatingAngle = isVROrMobile
                ? ((id - center) * -10 + (id + 1 - center) * -10) / 2
                : 0;

            const pointA = new Vector3(
                id * (width / ELEMENTS_NUMBER),
                0,
                isVROrMobile ? (Math.pow(id - center, 2) * height) / 4 : 0
            );
            const pointB = new Vector3(
                (id + 1) * (width / ELEMENTS_NUMBER),
                0,
                isVROrMobile ? (Math.pow(id + 1 - center, 2) * height) / 4 : 0
            );

            const direction = new Vector3().subVectors(pointA, pointB);
            const arrowLenght = direction.length();

            return (
                <a-entity
                    key={id}
                    position={`${(pointA.x + pointB.x) / 2} 0 ${
                        (pointA.z + pointB.z) / 2
                    }`}
                    rotation={`0 ${rotatingAngle} 0`}
                >
                    <a-cylinder
                        radius={ARROW_RADIUS}
                        height={isVROrMobile ? arrowLenght : ARROW_WIDTH}
                        color="#c6c6c6"
                        rotation="0 0 -90"
                    >
                        <a-cone
                            color="#c6c6c6"
                            radius-bottom={ARROW_RADIUS * 2}
                            radius-top={ARROW_RADIUS}
                            height={ARROW_WIDTH * 0.3}
                            position={`0 ${
                                (arrowLenght / 2 - BLOCK_WIDTH / 2) / 2
                            } 0`}
                        />
                    </a-cylinder>
                </a-entity>
            );
        });
    };

    return (
        <a-entity class="BlockChain" {...otherAttributes}>
            <a-entity
                position={`${
                    (ELEMENT_WIDTH * data.length - ELEMENT_WIDTH * 0.2) / -2 +
                    BLOCK_WIDTH / 2
                } 0 0`}
            >
                {drawBlocks()}
                {drawArrows()}
            </a-entity>
        </a-entity>
    );
}

// requiring dataPoints
BlockChain.propTypes = {
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    deviceData: PropTypes.object.isRequired
};

export default BlockChain;
